import { ref, computed, onMounted } from 'vue'

/**
 * Theme Manager Composable
 * 
 * Manages theme state, CSS variable injection, and dark mode
 */
const isDark = ref(false)
const themeLoaded = ref(false)
const themeVariables = ref({
  light: {},
  dark: {},
  global: {},
})

export function useTheme() {
  // Check if dark mode is preferred
  const prefersDark = computed(() => {
    if (typeof window === 'undefined') return false
    return window.matchMedia('(prefers-color-scheme: dark)').matches
  })

  // Toggle dark mode
  const toggleDark = () => {
    isDark.value = !isDark.value
    applyTheme()
    savePreference()
  }

  // Set dark mode explicitly
  const setDark = (value) => {
    isDark.value = value
    applyTheme()
    savePreference()
  }

  // Apply theme to document
  const applyTheme = () => {
    if (typeof document === 'undefined') return

    const html = document.documentElement
    
    // Set data attribute for CSS selectors
    html.setAttribute('data-theme', isDark.value ? 'dark' : 'light')
    
    // Apply CSS variables
    const mode = isDark.value ? 'dark' : 'light'
    const variables = themeVariables.value[mode] || {}
    
    Object.entries(variables).forEach(([key, value]) => {
      html.style.setProperty(key, value)
    })

    // Apply global variables
    const globalVars = themeVariables.value.global || {}
    Object.entries(globalVars).forEach(([key, value]) => {
      if (key === 'border_radius') {
        html.style.setProperty('--theme-border-radius', `${value}px`)
      } else {
        html.style.setProperty(`--theme-${key.replace(/_/g, '-')}`, value)
      }
    })

    // Load fonts if needed
    loadFonts(globalVars.heading_font, globalVars.body_font)
  }

  // Load Google Fonts
  const loadFonts = (headingFont, bodyFont) => {
    if (typeof document === 'undefined') return

    const fonts = new Set()
    if (headingFont) fonts.add(headingFont)
    if (bodyFont && bodyFont !== headingFont) fonts.add(bodyFont)

    if (fonts.size === 0) return

    const fontParam = Array.from(fonts).map(f => `family=${encodeURIComponent(f)}:wght@300;400;500;600;700;800`).join('&')
    const fontUrl = `https://fonts.googleapis.com/css2?${fontParam}&display=swap`

    // Check if already loaded
    const existingLink = document.querySelector(`link[href*="${fontUrl}"]`)
    if (existingLink) return

    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = fontUrl
    document.head.appendChild(link)
  }

  // Save user preference
  const savePreference = () => {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('theme-preference', isDark.value ? 'dark' : 'light')
    }
  }

  // Load user preference
  const loadPreference = () => {
    if (typeof localStorage === 'undefined') {
      isDark.value = prefersDark.value
      return
    }

    const saved = localStorage.getItem('theme-preference')
    if (saved) {
      isDark.value = saved === 'dark'
    } else {
      isDark.value = prefersDark.value
    }
  }

  // Set theme data from API
  const setThemeData = (data) => {
    themeVariables.value = {
      light: data.light || {},
      dark: data.dark || {},
      global: data.global || {},
    }
    themeLoaded.value = true
    applyTheme()
  }

  // Initialize theme
  const initTheme = () => {
    loadPreference()
    applyTheme()
  }

  // CSS class for transitions
  const themeTransitionClass = computed(() => {
    return themeLoaded.value ? 'theme-loaded' : ''
  })

  onMounted(() => {
    initTheme()
  })

  // Watch for system preference changes
  if (typeof window !== 'undefined') {
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
      if (!localStorage.getItem('theme-preference')) {
        isDark.value = e.matches
        applyTheme()
      }
    })
  }

  return {
    isDark,
    isLight: computed(() => !isDark.value),
    themeLoaded,
    toggleDark,
    setDark,
    applyTheme,
    setThemeData,
    initTheme,
    themeTransitionClass,
    prefersDark,
  }
}

export default useTheme
